'use client';

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BusinessUnitOption } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';

export const BUSINESS_UNITS_QUERY_KEY = 'business-units';

const NEW_BUSINESS_UNIT_COLOR = '#E65C32';

export function useBusinessUnits({ enabled = true }: { enabled?: boolean }) {
  return useQuery({
    queryKey: [BUSINESS_UNITS_QUERY_KEY],
    queryFn: () => terraProxyApiClient.getAllBusinessUnits(),
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useApplicationBusinessUnits({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  return useQuery({
    queryKey: [BUSINESS_UNITS_QUERY_KEY, 'application', applicationId],
    queryFn: async () => {
      if (!applicationId) {
        return [];
      }

      const response =
        await terraProxyApiClient.getApplicationBusinessUnits(applicationId);

      return response.map((item) => ({
        id: item.businessUnit.id,
        name: item.businessUnit.name,
      }));
    },
    enabled: Boolean(applicationId),
    refetchOnWindowFocus: false,
  });
}

export function useUpdateApplicationBusinessUnits({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (businessUnitIds: string[]) => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      return terraProxyApiClient.updateApplicationBusinessUnits({
        applicationId,
        businessUnitIds,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [BUSINESS_UNITS_QUERY_KEY, 'application', applicationId],
      }),
  });
}

export function useCreateBusinessUnitOption() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (name: string) =>
      terraProxyApiClient.createBusinessUnit({
        name,
        color: NEW_BUSINESS_UNIT_COLOR,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [BUSINESS_UNITS_QUERY_KEY] }),
  });

  const createBusinessUnitOption = useCallback(
    async (name: string): Promise<BusinessUnitOption> => {
      const created = await mutateAsync(name);

      return { id: created.id, name: created.name };
    },
    [mutateAsync],
  );

  return { createBusinessUnitOption, isCreatingBusinessUnit: isPending };
}
